/* eslint-disable */
import {
  Direction,
  FeatureGroup,
  FitBoundsOptions,
  IconOptions,
  LatLngBounds,
  Layer,
  Map,
  Marker,
  Point,
  Polyline,
  TooltipOptions,
} from 'leaflet'

import { environment } from '@environments/environment'
import { LatLng } from '@shared/models/latlng.model'
import { AbstractMapAdapter } from './abstract-map-adapter'
import { generalConfigMap, mapboxConfig } from '@app/core/constants/config-map'
import { DeviceStatus } from '../models/device.model'
import { VehicleMarker, VehicleModelType, VehicleType } from '../models/vehicle.model'
import { Coordinate } from '../models/coordinate.model'
import { StopMarker } from '@app/modules/client/map/shared/models/stop-marker.model'
import { TooltipDisplaySetting } from '../models/setting.model'
import { inject } from '@angular/core'
import { ApiService } from './api.service'

declare let L: any

declare module 'leaflet' {
  interface Map {
    toggleFullscreen: () => void
  }
}

export class AbstractMapboxAdapter extends AbstractMapAdapter {
  map: Map
  currentLayer: Layer

  private readonly apiService = inject(ApiService)
  private fileUrl = this.apiService?.fileUrl

  initMap(map: HTMLElement): void {
    L.mapbox.accessToken = environment.mapBoxAccessToken

    const mapOptions = {
      attributionControl: mapboxConfig.mapAttributionControl,
      minZoom: generalConfigMap.minZoom,
      maxZoom: generalConfigMap.maxZoom,
      worldCopyJump: mapboxConfig.mapWorldCopyJump,
      maxBounds: generalConfigMap.defaultMaxMapBounds,
      maxBoundsViscosity: mapboxConfig.mapMaxBoundsViscosity,
      zoomAnimation: true,
      fadeAnimation: false,
      markerZoomAnimation: true,
    }

    const defaultStyleUri = mapboxConfig.mapboxMapStyleStreets
    const stylePath = defaultStyleUri.replace('mapbox://styles/', '')

    const tileUrl = `https://api.mapbox.com/styles/v1/${stylePath}/tiles/512/{z}/{x}/{y}@2x?access_token=${environment.mapBoxAccessToken}`

    this.currentLayer = L.tileLayer(tileUrl, {
      tileSize: 512,
      zoomOffset: -1,
      attribution: '© Mapbox © OpenStreetMap',
    })

    this.map = L.map(map, mapOptions).addLayer(this.currentLayer)
  }

  fitBounds(coordinates: LatLng | LatLng[], options: FitBoundsOptions = {}): void {
    coordinates = !Array.isArray(coordinates) ? [coordinates] : coordinates
    const bounds: LatLngBounds = new L.LatLngBounds()
    const toValidCoordinate = (lat, lng) => new L.LatLng(lat, lng)
    const extendLatLngToBounds = ({ lat, lng }) => bounds.extend(toValidCoordinate(lat, lng))

    coordinates.filter(Boolean).forEach(extendLatLngToBounds)

    bounds.isValid() && this.map.fitBounds(bounds, options)
    this.map.invalidateSize()
  }

  protected createVehicleMarker(
    vehicleMarker: VehicleMarker,
    isMapMarkerTooltipShown = true,
    isAsset = false,
    tooltipDisplaySettings = TooltipDisplaySetting.TitleRegno,
  ) {
    const { id, device, lng, lat, regNo, defaultLabel, type, customVehicleType, driverDetails } = vehicleMarker

    const angle: number = device.trackerData.angle
    const coordinates: LatLng = { lat, lng }
    const deviceStatus: DeviceStatus = device.status
    const iconUrl: string = this.getMarkerIcon(type, deviceStatus, customVehicleType, isAsset)
    const marker: Marker = this.createMarker(id, iconUrl, coordinates, angle, deviceStatus)

    const tooltips = {
      [TooltipDisplaySetting.Regno]: `<span><strong>${regNo}</strong></span>`,
      [TooltipDisplaySetting.Title]: `<span>${defaultLabel}</span>`,
      [TooltipDisplaySetting.TitleRegno]: `<span><strong>${regNo}</strong> </span><span>${defaultLabel}</span>`,
      [TooltipDisplaySetting.Drivers]: driverDetails ? `<span><strong>${driverDetails.fullName}</strong></span>` : null,
      [TooltipDisplaySetting.RegnoDriver]: driverDetails
        ? `<span><strong>${regNo}</strong></span> <span>${driverDetails.fullName}</span>`
        : `<span><strong>${regNo}</strong></span>`,
      [TooltipDisplaySetting.TitleDriver]: driverDetails
        ? `<span>${defaultLabel}</span> <span>${driverDetails.fullName}</span>`
        : `<span>${defaultLabel}</span>`,
    }

    let vehicleTooltip = tooltips[tooltipDisplaySettings]

    if (!vehicleTooltip && tooltipDisplaySettings === TooltipDisplaySetting.Drivers) {
      vehicleTooltip = tooltips[TooltipDisplaySetting.TitleRegno]
    }

    this.bindTooltip(marker, vehicleTooltip, mapboxConfig.tooltipClassVehicleMarker, isMapMarkerTooltipShown)

    return marker
  }

  protected createMarker(
    id: number | string,
    iconUrl: string,
    coordinates: LatLng,
    angle: number,
    status: DeviceStatus,
    anchor?: Point,
  ): Marker {
    const position: LatLng = new L.LatLng(coordinates.lat, coordinates.lng) as LatLng
    const tooltipAnchor: Point = new L.Point(mapboxConfig.tooltipAnchorX, mapboxConfig.tooltipAnchorY)
    const iconSize: [number, number] = mapboxConfig.iconDefaultSize as [number, number]
    const iconOptions: IconOptions & { status: DeviceStatus } = {
      iconUrl,
      tooltipAnchor,
      status,
      iconSize,
    }

    const marker = document.createElement('div')
    marker.className = 'marker'
    marker.style.backgroundImage = `url(${iconUrl})`

    const markerOptions = {
      id,
      anchor,
      icon: new L.DivIcon({
        html: marker.outerHTML,
        ...iconOptions,
      }),
    }

    return L.marker(position, markerOptions).setRotationAngle(angle).setRotationOrigin(mapboxConfig.iconRotationOrigin)
  }

  private assetMarkerUrl(status): string {
    const markerAssetBaseUrl = 'assets/images/icons/navigation_asset'

    const markerIconAsset = (deviceStatus: DeviceStatus | null) =>
      deviceStatus ? `${markerAssetBaseUrl}_${deviceStatus}.svg` : `${markerAssetBaseUrl}.svg`

    switch (status) {
      case DeviceStatus.Driving:
      case DeviceStatus.Idle:
        return markerIconAsset(status)
      default:
        return markerIconAsset(null)
    }
  }

  private vehicleMarkerUrlDefault(type: VehicleType, color: DeviceStatus | 'black'): string {
    const vehicleType: string = type.toLowerCase()

    return `assets/images/map-icons/${color}-${vehicleType}.png`
  }

  private vehicleMarkerUrl(type: VehicleType, status: DeviceStatus, vehicleTypes: VehicleModelType): string {
    const urlByStatus = (status: DeviceStatus) => {
      const mappedStatus = status === DeviceStatus.Idle ? 'idling' : status
      return vehicleTypes && `${this.fileUrl}${vehicleTypes?.[mappedStatus]}`
    }

    switch (status) {
      case DeviceStatus.Driving:
      case DeviceStatus.Idle:
      case DeviceStatus.Stopped:
      case DeviceStatus.Towing:
        return urlByStatus(status)
      default:
        return this.vehicleMarkerUrlDefault(type, 'black')
    }
  }

  protected getMarkerIcon(
    type: VehicleType = VehicleType.Car,
    status: DeviceStatus,
    vehicleTypes: VehicleModelType,
    isAsset = false,
  ): string {
    if (isAsset) {
      return this.assetMarkerUrl(status)
    }
    if (vehicleTypes) {
      return this.vehicleMarkerUrl(type, status, vehicleTypes)
    }
    return this.vehicleMarkerUrlDefault(type, 'black')
  }

  protected bindTooltip(marker: Marker, content: string, className: string, permanent = false): void {
    const direction: Direction = mapboxConfig.tooltipDirection as Direction
    const tooltipOptions: TooltipOptions = {
      direction,
      permanent,
      className,
    }

    marker.bindTooltip(content, tooltipOptions).openTooltip()
  }

  protected createRoute(coordinates: (LatLng | Coordinate)[], currentRoute: boolean = false, options?: { [key: string]: any }): Polyline {
    const latLng: LatLng[] = this.createValidCoordinates(coordinates)
    const polylineOptions = {
      color: currentRoute ? generalConfigMap.polylineColorCurrentRoute : generalConfigMap.polylineColor,
      weight: generalConfigMap.polylineWeight,
      zIndex: currentRoute ? generalConfigMap.zIndexCurrentRoute : generalConfigMap.defaultZIndex,
      ...options,
    }

    return new L.Polyline(latLng, polylineOptions)
  }

  protected createValidCoordinates(coordinates: LatLng[]): LatLng[] {
    const toLatLng = ({ lat, lng }: Coordinate) => new L.LatLng(lat, lng)

    return coordinates.filter(Boolean).map(toLatLng)
  }

  protected createIconSequence(polyline: Polyline): FeatureGroup {
    const polylineDecoratorOptions = {
      patterns: [
        {
          offset: mapboxConfig.iconSequenceOffset,
          repeat: mapboxConfig.iconSequenceRepeat,
          symbol: new L.Symbol.arrowHead({
            pixelSize: mapboxConfig.iconSequencePixelSize,
            polygon: mapboxConfig.iconSequencePolygon,
            pathOptions: { stroke: mapboxConfig.iconSequenceStroke },
          }),
        },
      ],
    }

    return L.polylineDecorator(polyline, polylineDecoratorOptions)
  }

  protected createStopMarker(stopMarker: StopMarker): Marker {
    const { id, coordinates, indexStop, duration } = stopMarker
    const iconAnchor: Point = new L.Point(generalConfigMap.iconAnchorX, generalConfigMap.iconAnchorY)
    const marker: Marker = this.createMarker(id, mapboxConfig.iconDefaultUrl, coordinates, mapboxConfig.iconDefaultAngle, null, iconAnchor)
    const tooltipHoverContent = `<div class="stop-marker-tooltip-content"><span>${indexStop}</span><span>${duration}</span></div>`
    const tooltipHoverClasses = `${mapboxConfig.tooltipClassStopMarker} ${mapboxConfig.tooltipClassStopMarkerHover}`

    this.bindTooltip(marker, indexStop, mapboxConfig.tooltipClassStopMarker, true)
    this.markerTooltiplistener('mouseover', marker, tooltipHoverContent, tooltipHoverClasses)
    this.markerTooltiplistener('mouseout', marker, indexStop, mapboxConfig.tooltipClassStopMarker)

    return marker
  }

  private markerTooltiplistener(eventName: string, marker: Marker, content: string, className: string): void {
    const onEventHandle = (): void => {
      marker.unbindTooltip()
      this.bindTooltip(marker, content, className, true)
    }

    marker.on(eventName, onEventHandle)
  }
}
